import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import type { ReactNode } from 'react'
import type {
  EditableThemeTokenName,
  EditableThemeTokensByVariant,
  ThemeMode,
  ThemePresetId,
  ThemeVariant,
  WorkspaceThemeTokensFromApi,
} from '@/shared/theme/theme-config'
import {
  currentEditableThemeTokens,
  defaultEditableThemeTokens,
  mapWorkspaceTokensToKebab,
  resolveThemePreset,
} from '@/shared/theme/theme-config'
import {
  getStoredMode,
  getStoredPreset,
  persistThemeState,
  resolveEffectiveMode,
} from '@/shared/theme/theme-runtime'
import { useCurrentUserProfile } from '@/entities/user/model/use-current-user-profile'

interface ThemeContextValue {
  mode: ThemeMode
  preset: ThemePresetId
  isDark: boolean
  effectiveMode: ThemeVariant
  isHydrated: boolean
  editableTokens: EditableThemeTokensByVariant
  workspaceTokens: EditableThemeTokensByVariant | null
  setMode: (mode: ThemeMode) => void
  setPreset: (preset: ThemePresetId) => void
  updateCustomToken: (params: {
    variant: ThemeVariant
    token: EditableThemeTokenName
    value: string
  }) => void
  replaceCustomTokens: (tokens: EditableThemeTokensByVariant) => void
  resetCustomTokens: () => void
}

const ThemeContext = createContext<ThemeContextValue | null>(null)

function mergeTokens({
  base,
  workspace,
  local,
}: {
  base: EditableThemeTokensByVariant
  workspace: EditableThemeTokensByVariant | null
  local: EditableThemeTokensByVariant | null
}) {
  return {
    light: {
      ...base.light,
      ...(workspace?.light ?? {}),
      ...(local?.light ?? {}),
    },
    dark: {
      ...base.dark,
      ...(workspace?.dark ?? {}),
      ...(local?.dark ?? {}),
    },
  }
}

function applyTokensToDom({
  mode,
  preset,
  workspaceTokens,
  localTokens,
  setIsDark,
}: {
  mode: ThemeMode
  preset: ThemePresetId
  workspaceTokens: EditableThemeTokensByVariant | null
  localTokens: EditableThemeTokensByVariant | null
  setIsDark: (isDark: boolean) => void
}) {
  const effectiveMode = resolveEffectiveMode(mode)

  if (effectiveMode === 'dark') {
    document.documentElement.classList.add('dark')
  } else {
    document.documentElement.classList.remove('dark')
  }

  setIsDark(effectiveMode === 'dark')

  const resolvedPreset = resolveThemePreset({
    preset,
    workspaceTokens,
  })
  const resolvedTokens = resolvedPreset[effectiveMode]

  for (const [tokenName, tokenValue] of Object.entries(resolvedTokens)) {
    document.documentElement.style.setProperty(`--${tokenName}`, tokenValue)
  }

  const merged = mergeTokens({
    base:
      preset === 'current'
        ? currentEditableThemeTokens
        : defaultEditableThemeTokens,
    workspace: preset === 'workspace' ? workspaceTokens : null,
    local: localTokens,
  })
  const overrides = merged[effectiveMode]

  for (const [tokenName, tokenValue] of Object.entries(overrides)) {
    document.documentElement.style.setProperty(`--${tokenName}`, tokenValue)
  }
}

function useApplyThemeEffect({
  mode,
  preset,
  workspaceTokens,
  localTokens,
  setIsDark,
}: {
  mode: ThemeMode
  preset: ThemePresetId
  workspaceTokens: EditableThemeTokensByVariant | null
  localTokens: EditableThemeTokensByVariant | null
  setIsDark: (isDark: boolean) => void
}) {
  useEffect(() => {
    const apply = () =>
      applyTokensToDom({
        mode,
        preset,
        workspaceTokens,
        localTokens,
        setIsDark,
      })

    apply()

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    const handleChange = () => {
      if (mode === 'system') {
        apply()
      }
    }

    mediaQuery.addEventListener('change', handleChange)

    return () => {
      mediaQuery.removeEventListener('change', handleChange)
    }
  }, [mode, preset, workspaceTokens, localTokens, setIsDark])
}

function useCustomTokenMutations(
  setLocalTokens: React.Dispatch<
    React.SetStateAction<EditableThemeTokensByVariant | null>
  >,
) {
  const updateCustomToken = useCallback(
    ({
      variant,
      token,
      value,
    }: {
      variant: ThemeVariant
      token: EditableThemeTokenName
      value: string
    }) => {
      setLocalTokens((current) => ({
        light: {
          ...currentEditableThemeTokens.light,
          ...(current?.light ?? {}),
          ...(variant === 'light' ? { [token]: value } : {}),
        },
        dark: {
          ...currentEditableThemeTokens.dark,
          ...(current?.dark ?? {}),
          ...(variant === 'dark' ? { [token]: value } : {}),
        },
      }))
    },
    [setLocalTokens],
  )

  const replaceCustomTokens = useCallback(
    (nextTokens: EditableThemeTokensByVariant) => {
      setLocalTokens(nextTokens)
    },
    [setLocalTokens],
  )

  const resetCustomTokens = useCallback(() => {
    setLocalTokens(null)
  }, [setLocalTokens])

  return { updateCustomToken, replaceCustomTokens, resetCustomTokens }
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const { currentUser } = useCurrentUserProfile()
  const workspaceTokensFromApi = mapWorkspaceTokensToKebab(
    (currentUser?.workspace
      ?.customThemeTokens as WorkspaceThemeTokensFromApi) ?? null,
  )

  const [mode, setModeState] = useState<ThemeMode>(() => getStoredMode())
  const [preset, setPresetState] = useState<ThemePresetId>(() =>
    getStoredPreset(),
  )
  const [localTokens, setLocalTokens] =
    useState<EditableThemeTokensByVariant | null>(null)
  const [isDark, setIsDark] = useState(false)

  const modeRef = useRef(mode)
  const presetRef = useRef(preset)
  modeRef.current = mode
  presetRef.current = preset

  const setMode = useCallback((nextMode: ThemeMode) => {
    setModeState(nextMode)
    persistThemeState({ mode: nextMode, preset: presetRef.current })
  }, [])

  const setPreset = useCallback((nextPreset: ThemePresetId) => {
    setPresetState(nextPreset)
    persistThemeState({ mode: modeRef.current, preset: nextPreset })
  }, [])

  const editableTokens = mergeTokens({
    base:
      preset === 'current'
        ? currentEditableThemeTokens
        : defaultEditableThemeTokens,
    workspace: preset === 'workspace' ? workspaceTokensFromApi : null,
    local: localTokens,
  })

  useApplyThemeEffect({
    mode,
    preset,
    workspaceTokens: workspaceTokensFromApi,
    localTokens,
    setIsDark,
  })

  const { updateCustomToken, replaceCustomTokens, resetCustomTokens } =
    useCustomTokenMutations(setLocalTokens)

  return (
    <ThemeContext.Provider
      value={{
        mode,
        preset,
        isDark,
        effectiveMode: isDark ? 'dark' : 'light',
        isHydrated: true,
        editableTokens,
        workspaceTokens: workspaceTokensFromApi,
        setMode,
        setPreset,
        updateCustomToken,
        replaceCustomTokens,
        resetCustomTokens,
      }}
    >
      {children}
    </ThemeContext.Provider>
  )
}

export function useTheme(): ThemeContextValue {
  const context = useContext(ThemeContext)
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider')
  }

  return context
}
